import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosUtil";

// ─── Helpers ────────────────────────────────────────────────────────────────
function splitCSV(str) {
  return (str || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function buildSegments(type, application) {
  const segments = {};
  if (type) segments["By Type"] = splitCSV(type);
  if (application) segments["By Application"] = splitCSV(application);
  return segments;
}

// ─── Status badge ────────────────────────────────────────────────────────────
function StatusBadge({ item }) {
  if (!item.isGenerated)
    return (
      <span style={{ padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: "#fef3c7", color: "#92400e" }}>
        Pending
      </span>
    );
  return (
    <span style={{ padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: "#d1fae5", color: "#065f46", display: "inline-flex", alignItems: "center", gap: 4 }}>
      <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
      {item.generationType === "manual" ? "Created (Manual)" : "Created (AI)"}
    </span>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function ReportRegistry() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(50);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filterGenerated, setFilterGenerated] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // AI Generate modal state
  const [aiModal, setAiModal] = useState(null); // registry item
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiStatus, setAiStatus] = useState("");

  // Import state
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const searchTimeout = useRef(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { page, limit };
      if (search) params.search = search;
      if (filterGenerated !== "") params.generated = filterGenerated;
      const { data } = await axiosInstance.get("/report-registry", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load registry");
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, filterGenerated]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Debounced search
  const handleSearchInput = (val) => {
    setSearchInput(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
    }, 400);
  };

  // ─── AI Generate ─────────────────────────────────────────────────────────
  const handleAiGenerate = async () => {
    if (!aiModal) return;
    setAiGenerating(true);
    setAiStatus("Generating report with AI...");
    try {
      const { data } = await axiosInstance.post("/reports", {
        title: `Global ${aiModal.keyword} Market Size, Share and Forecast to ${new Date().getFullYear() + 9}`,
        industry: aiModal.category || "General",
      });
      // Mark as generated in registry
      await axiosInstance.patch(`/report-registry/${aiModal._id}/mark-generated`, {
        reportId: data._id,
        type: "ai",
      });
      setAiStatus("Report generated successfully!");
      // Update local state
      setItems((prev) =>
        prev.map((i) =>
          i._id === aiModal._id ? { ...i, isGenerated: true, generationType: "ai" } : i
        )
      );
      setTimeout(() => { setAiModal(null); setAiStatus(""); }, 1800);
    } catch (err) {
      setAiStatus(err?.response?.data?.message || "Generation failed");
    } finally {
      setAiGenerating(false);
    }
  };

  // ─── Manual Generate ─────────────────────────────────────────────────────
  const handleManualGenerate = (item) => {
    // Build state to prefill CreateManualReport
    const prefill = {
      registryId: item._id,
      title: `Global ${item.keyword} Market Size, Share and Forecast to ${new Date().getFullYear() + 9}`,
      marketName: `Global ${item.keyword} Market`,
      industry: item.category || "",
      players: splitCSV(item.keyPlayers),
      segments: buildSegments(item.type, item.application),
    };
    navigate("/admin/reports/create-manual", { state: { prefill } });
  };

  // ─── Import Excel ─────────────────────────────────────────────────────────
  const handleImport = async () => {
    if (!window.confirm("Import all rows from the Excel file? This may take a while for large files.")) return;
    setImporting(true);
    setImportResult(null);
    try {
      const { data } = await axiosInstance.post("/report-registry/import-excel", {
        filePath: "F:/report-gen/All Market Reports Data.xlsx",
      });
      setImportResult(data);
      fetchItems();
    } catch (err) {
      setImportResult({ error: err?.response?.data?.message || "Import failed" });
    } finally {
      setImporting(false);
    }
  };

  // ─── Pagination helpers ──────────────────────────────────────────────────
  const pageNumbers = () => {
    const pages = [];
    const delta = 2;
    for (let i = Math.max(1, page - delta); i <= Math.min(totalPages, page + delta); i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f3f4f6" }}>
      {/* Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111827" }}>Report Registry</h1>
          <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
            {total.toLocaleString()} reports from Excel &bull; {items.filter((i) => i.isGenerated).length} generated this page
          </p>
        </div>
        <button
          onClick={handleImport}
          disabled={importing}
          style={{
            padding: "8px 18px", borderRadius: 7, border: "none", cursor: "pointer",
            background: importing ? "#9ca3af" : "#6366f1", color: "#fff", fontWeight: 600, fontSize: 13,
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          {importing ? (
            <>
              <span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
              Importing...
            </>
          ) : (
            <>
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                <polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /><path d="M9 18H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4" />
              </svg>
              Import from Excel
            </>
          )}
        </button>
      </div>

      {/* Import result */}
      {importResult && (
        <div style={{ margin: "12px 24px", padding: "10px 16px", borderRadius: 7, background: importResult.error ? "#fee2e2" : "#d1fae5", color: importResult.error ? "#991b1b" : "#065f46", fontSize: 13 }}>
          {importResult.error
            ? importResult.error
            : `Import complete — ${importResult.inserted?.toLocaleString()} new, ${importResult.skipped?.toLocaleString()} already existed out of ${importResult.totalRows?.toLocaleString()} rows.`}
        </div>
      )}

      {/* Filters */}
      <div style={{ padding: "14px 24px", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        {/* Search */}
        <div style={{ position: "relative", flex: "1 1 260px", maxWidth: 380 }}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
            style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search keyword or category..."
            value={searchInput}
            onChange={(e) => handleSearchInput(e.target.value)}
            style={{ width: "100%", padding: "7px 10px 7px 32px", border: "1px solid #d1d5db", borderRadius: 7, fontSize: 13, outline: "none", background: "#fff" }}
          />
        </div>

        {/* Status filter */}
        <select
          value={filterGenerated}
          onChange={(e) => { setFilterGenerated(e.target.value); setPage(1); }}
          style={{ padding: "7px 12px", border: "1px solid #d1d5db", borderRadius: 7, fontSize: 13, background: "#fff", outline: "none" }}
        >
          <option value="">All Status</option>
          <option value="false">Pending</option>
          <option value="true">Generated</option>
        </select>

        <span style={{ marginLeft: "auto", fontSize: 13, color: "#6b7280" }}>
          Page {page} of {totalPages} &bull; {total.toLocaleString()} total
        </span>
      </div>

      {/* Table */}
      <div style={{ margin: "0 24px 24px", background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#6b7280", fontSize: 14 }}>Loading...</div>
        ) : error ? (
          <div style={{ padding: 20, color: "#dc2626", fontSize: 13 }}>{error}</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#6b7280", fontSize: 14 }}>
            No records found.{" "}
            <button onClick={handleImport} style={{ background: "none", border: "none", color: "#6366f1", cursor: "pointer", fontWeight: 600 }}>
              Import from Excel
            </button>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                  {["#ID", "Keyword / Report Name", "Type Segments", "Application Segments", "Category", "Status", "Actions"].map((h) => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, color: "#374151", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={item._id} style={{ borderBottom: "1px solid #f3f4f6", background: item.isGenerated ? "#f0fdf4" : idx % 2 === 0 ? "#fff" : "#fafafa" }}>
                    <td style={{ padding: "9px 14px", color: "#9ca3af", fontSize: 11, whiteSpace: "nowrap" }}>{item.excelId}</td>
                    <td style={{ padding: "9px 14px", maxWidth: 260 }}>
                      <div style={{ fontWeight: 600, color: "#111827", lineHeight: 1.35 }}>{item.keyword}</div>
                    </td>
                    <td style={{ padding: "9px 14px", color: "#4b5563", maxWidth: 180, fontSize: 12 }}>
                      {item.type ? (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                          {splitCSV(item.type).map((t) => (
                            <span key={t} style={{ background: "#e0e7ff", color: "#4338ca", borderRadius: 4, padding: "1px 6px", fontSize: 11 }}>{t}</span>
                          ))}
                        </div>
                      ) : <span style={{ color: "#d1d5db" }}>—</span>}
                    </td>
                    <td style={{ padding: "9px 14px", color: "#4b5563", maxWidth: 200, fontSize: 12 }}>
                      {item.application ? (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                          {splitCSV(item.application).map((a) => (
                            <span key={a} style={{ background: "#fef3c7", color: "#92400e", borderRadius: 4, padding: "1px 6px", fontSize: 11 }}>{a}</span>
                          ))}
                        </div>
                      ) : <span style={{ color: "#d1d5db" }}>—</span>}
                    </td>
                    <td style={{ padding: "9px 14px", fontSize: 12, color: "#374151", whiteSpace: "nowrap" }}>{item.category || "—"}</td>
                    <td style={{ padding: "9px 14px", whiteSpace: "nowrap" }}><StatusBadge item={item} /></td>
                    <td style={{ padding: "9px 14px", whiteSpace: "nowrap" }}>
                      {item.isGenerated ? (
                        <span style={{ color: "#6b7280", fontSize: 12 }}>Done</span>
                      ) : (
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            onClick={() => { setAiModal(item); setAiStatus(""); }}
                            style={{ padding: "4px 12px", borderRadius: 5, border: "none", background: "#6366f1", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                          >
                            AI Generate
                          </button>
                          <button
                            onClick={() => handleManualGenerate(item)}
                            style={{ padding: "4px 12px", borderRadius: 5, border: "1px solid #d1d5db", background: "#fff", color: "#374151", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                          >
                            Manual
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div style={{ padding: "12px 16px", borderTop: "1px solid #e5e7eb", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <button onClick={() => setPage(1)} disabled={page === 1} style={pageBtnStyle(page === 1)}>«</button>
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} style={pageBtnStyle(page === 1)}>‹</button>
            {pageNumbers().map((p) => (
              <button key={p} onClick={() => setPage(p)} style={pageBtnStyle(false, p === page)}>{p}</button>
            ))}
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={pageBtnStyle(page === totalPages)}>›</button>
            <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={pageBtnStyle(page === totalPages)}>»</button>
            <span style={{ marginLeft: 8, fontSize: 12, color: "#9ca3af" }}>
              Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total.toLocaleString()}
            </span>
          </div>
        )}
      </div>

      {/* AI Generate Modal */}
      {aiModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: "28px 32px", maxWidth: 480, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            <h2 style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 700, color: "#111827" }}>AI Generate Report</h2>
            <p style={{ margin: "0 0 16px", fontSize: 13, color: "#6b7280" }}>The following information will be sent to the AI generation API.</p>

            <div style={{ background: "#f9fafb", borderRadius: 8, padding: "14px 16px", marginBottom: 16 }}>
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em" }}>Report Title</span>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", marginTop: 2 }}>
                  Global {aiModal.keyword} Market Size, Share and Forecast to {new Date().getFullYear() + 9}
                </div>
              </div>
              <div>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em" }}>Category / Industry</span>
                <div style={{ fontSize: 13, color: "#374151", marginTop: 2 }}>{aiModal.category || "—"}</div>
              </div>
            </div>

            {aiStatus && (
              <div style={{ padding: "8px 12px", borderRadius: 6, marginBottom: 14, background: aiStatus.includes("fail") || aiStatus.includes("error") ? "#fee2e2" : aiStatus.includes("success") ? "#d1fae5" : "#ede9fe", color: aiStatus.includes("fail") || aiStatus.includes("error") ? "#991b1b" : aiStatus.includes("success") ? "#065f46" : "#4f46e5", fontSize: 13 }}>
                {aiStatus}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => { setAiModal(null); setAiStatus(""); }}
                disabled={aiGenerating}
                style={{ padding: "8px 18px", borderRadius: 7, border: "1px solid #d1d5db", background: "#fff", color: "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                onClick={handleAiGenerate}
                disabled={aiGenerating}
                style={{ padding: "8px 20px", borderRadius: 7, border: "none", background: aiGenerating ? "#a5b4fc" : "#6366f1", color: "#fff", fontSize: 13, fontWeight: 600, cursor: aiGenerating ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 6 }}
              >
                {aiGenerating && <span style={{ display: "inline-block", width: 13, height: 13, border: "2px solid rgba(255,255,255,0.5)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />}
                {aiGenerating ? "Generating..." : "Generate Now"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

function pageBtnStyle(disabled, active) {
  return {
    padding: "4px 10px", borderRadius: 5, border: "1px solid #e5e7eb", fontSize: 13,
    background: active ? "#6366f1" : disabled ? "#f3f4f6" : "#fff",
    color: active ? "#fff" : disabled ? "#d1d5db" : "#374151",
    cursor: disabled ? "not-allowed" : "pointer", fontWeight: active ? 700 : 400,
    minWidth: 32, textAlign: "center",
  };
}
